import type { Editor } from '@tiptap/react';
import type { Transaction } from '@tiptap/pm/state';
import { useMemo, useCallback, useRef, useEffect } from 'react';
import { debounce } from '../../../../../utils/debounce';
import { UndoRedoManager } from '../../../UndoRedo/UndoRedoManager';
import { TiptapUndoRedoCommand } from '../TiptapUndoRedo/TiptapUndoRedoCommand';
import { useBlockContent } from '../../../Sections/useBlockContent';
import { useDraggableBlockManipulations } from '../../DraggableGridBlock/useDraggableBlockManipulations';
import { useBlockDimensionChangedHandlerWithoutUndoRedo } from '../../../hooks/UseBlockDimensionChangedHandler';
import { roundToNearestMultipleOfGridSize } from '../../gridHelper';

type BlockDimensionState = {
  widthPx: number;
  heightPx: number;
  yAxisPx: number;
  xAxisPx: number;
};

export function useTiptapUndoRedoCommands(editor: Editor | null) {
  const undoRedoManager = UndoRedoManager.getUndoRedoManager();

  const createUndoRedoCommand = useCallback(
    (editor: Editor, tr: Transaction) => {
      if (!tr.docChanged || !editor.can().undo()) return;

      undoRedoManager.pushUndoRedoCommands(new TiptapUndoRedoCommand(editor));
    },
    [undoRedoManager]
  );
  const onTransaction = useCallback(
    ({ editor, transaction }: { editor: Editor; transaction: Transaction }) => {
      createUndoRedoCommand(editor, transaction);
    },
    [createUndoRedoCommand]
  );

  useEffect(() => {
    if (!editor) return;

    editor.on('transaction', onTransaction);

    return () => {
      editor.off('transaction', onTransaction);
    };
  }, [editor, onTransaction]);

  return { onTransaction };
}

export function useTiptapUndoRedoCommandsWithDimensionChange(editor: Editor | null, blockId: string, sectionId: string) {
  const undoRedoManager = UndoRedoManager.getUndoRedoManager();
  const blockContent = useBlockContent(blockId);

  const blockDimensionChangedHandler = useBlockDimensionChangedHandlerWithoutUndoRedo();
  const { setDocumentHeight, getAdjustBlockHeight } = useDraggableBlockManipulations();

  // Track resize state
  const stateBeforeResizeRef = useRef<BlockDimensionState | null>(null);
  const stateAfterResizeRef = useRef<BlockDimensionState | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const hasResizedRef = useRef<boolean>(false);
  const prevBlockStateRef = useRef<BlockDimensionState | null>(null);
  const observerTriggeredResizeRef = useRef<boolean>(false);

  // Use refs so handleResize reads the latest values without being recreated on
  // every blockContent change, which would otherwise recreate the ResizeObserver.
  const blockContentRef = useRef(blockContent);
  blockContentRef.current = blockContent;

  const getAdjustBlockHeightRef = useRef(getAdjustBlockHeight);
  getAdjustBlockHeightRef.current = getAdjustBlockHeight;

  const blockDimensionChangedHandlerRef = useRef(blockDimensionChangedHandler);
  blockDimensionChangedHandlerRef.current = blockDimensionChangedHandler;

  const setDocumentHeightRef = useRef(setDocumentHeight);
  setDocumentHeightRef.current = setDocumentHeight;

  const resetResizeState = () => {
    stateBeforeResizeRef.current = null;
    stateAfterResizeRef.current = null;
    hasResizedRef.current = false;
    observerTriggeredResizeRef.current = false;
  };

  useEffect(() => {
    const currentBlockState: BlockDimensionState | null = blockContent?.blockConfig
      ? {
          widthPx: blockContent.blockConfig.width,
          heightPx: blockContent.blockConfig.height,
          yAxisPx: blockContent.blockConfig.y,
          xAxisPx: blockContent.blockConfig.x,
        }
      : null;

    if (currentBlockState && prevBlockStateRef.current) {
      const dimensionsChanged =
        currentBlockState.widthPx !== prevBlockStateRef.current.widthPx ||
        currentBlockState.heightPx !== prevBlockStateRef.current.heightPx ||
        currentBlockState.xAxisPx !== prevBlockStateRef.current.xAxisPx ||
        currentBlockState.yAxisPx !== prevBlockStateRef.current.yAxisPx;

      // If dimensions changed externally and it wasn't triggered by observer,
      // it's likely a manual resize - so reset the state
      if (dimensionsChanged && !observerTriggeredResizeRef.current) {
        resetResizeState();
      }

      observerTriggeredResizeRef.current = false;
    }

    prevBlockStateRef.current = currentBlockState;
  }, [blockContent]);

  const handleResize = useMemo(
    () =>
      debounce(async (height: number): Promise<void> => {
        const currentBlockContent = blockContentRef.current;
        if (!currentBlockContent?.blockConfig) return;

        const { blockConfig } = currentBlockContent;

        observerTriggeredResizeRef.current = true;

        const currentBlockState: BlockDimensionState = {
          widthPx: blockConfig.width,
          heightPx: blockConfig.height,
          yAxisPx: blockConfig.y,
          xAxisPx: blockConfig.x,
        };

        // Save state before if not already saved
        if (!stateBeforeResizeRef.current && !hasResizedRef.current) {
          stateBeforeResizeRef.current = currentBlockState;
          hasResizedRef.current = true;
        }

        const resizedBlock = { ...blockConfig };
        const adjustedBlockHeight = getAdjustBlockHeightRef.current(resizedBlock);
        const adjustedTextboxHeight = adjustedBlockHeight > height ? adjustedBlockHeight : height;
        const adjustedTextboxRoundedHeight = roundToNearestMultipleOfGridSize(adjustedTextboxHeight);

        const wasDimensionChangedOrNull =
          (resizedBlock.height && adjustedTextboxRoundedHeight > resizedBlock.height) || !resizedBlock.height;

        if (!wasDimensionChangedOrNull) return;

        const stateAfterResizing: BlockDimensionState = {
          widthPx: resizedBlock.width,
          heightPx: adjustedTextboxRoundedHeight,
          yAxisPx: resizedBlock.y,
          xAxisPx: resizedBlock.x,
        };

        await blockDimensionChangedHandlerRef.current(sectionId, blockId, stateAfterResizing);

        resizedBlock.height = adjustedTextboxRoundedHeight;
        setDocumentHeightRef.current(resizedBlock);

        stateAfterResizeRef.current = stateAfterResizing;
      }, 200),
    [blockId, sectionId]
  );

  const debouncedCreateUndoRedoCommand = useMemo(
    () =>
      debounce((editor: Editor, tr: Transaction) => {
        if (!tr.docChanged || !editor.can().undo()) return;

        // Skip if this is an undo transaction (will be handled by undo manager)
        const history = tr.getMeta('history$');
        if (history && !history.redo) return;

        const stateBefore = stateBeforeResizeRef.current;
        const stateAfter = stateAfterResizeRef.current;

        if (stateBefore && stateAfter) {
          const undoCallback = async (): Promise<void> => {
            if (stateBefore) {
              await blockDimensionChangedHandlerRef.current(sectionId, blockId, stateBefore);
            }
          };

          const redoCallback = async (): Promise<void> => {
            if (stateAfter) {
              await blockDimensionChangedHandlerRef.current(sectionId, blockId, stateAfter);
            }
          };

          undoRedoManager.pushUndoRedoCommands(new TiptapUndoRedoCommand(editor, undoCallback, redoCallback));
        } else {
          undoRedoManager.pushUndoRedoCommands(new TiptapUndoRedoCommand(editor));
        }

        resetResizeState();
      }, 500),
    [blockId, sectionId]
  );

  const onTransaction = useCallback(
    ({ editor, transaction }: { editor: Editor; transaction: Transaction }) => {
      debouncedCreateUndoRedoCommand(editor, transaction);
    },
    [debouncedCreateUndoRedoCommand]
  );

  useEffect(() => {
    if (!editor) return;

    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        handleResize(height);
      }
    });

    resizeObserverRef.current.observe(editor.view.dom);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [editor, handleResize]);

  useEffect(() => {
    if (!editor) return;

    editor.on('transaction', onTransaction);

    return () => {
      editor.off('transaction', onTransaction);
    };
  }, [editor, onTransaction]);
}
